import { useState } from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { ArrowRight, ChevronDown, ChevronUp, Mail, Linkedin, Github, Twitter } from 'lucide-react'

const Home = () => {
  const [currentTestimonial, setCurrentTestimonial] = useState(0)
  const [expandedTestimonials, setExpandedTestimonials] = useState<{ [key: number]: boolean }>({})

  const toggleTestimonial = (index: number) => {
    setExpandedTestimonials(prev => ({
      ...prev,
      [index]: !prev[index]
    }))
  }

  const highlightWords = (text: string, wordsToHighlight: string[]) => {
    let highlightedText = text
    wordsToHighlight.forEach(word => {
      const regex = new RegExp(`\\b${word}\\b`, 'gi')
      highlightedText = highlightedText.replace(regex, `<span class="text-gray-900 dark:text-gray-100">${word}</span>`)
    })
    return highlightedText
  }
  
  const topSkills = [
    'React',
    'TypeScript',
    'AI Integrations (OpenAI)',
    'Prompt Engineering',
    'Data-driven UIs',
    'Next.js API Routes',
    'HTML5 & (S)CSS3',
    'Cypress'
  ]



  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="pt-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            {/* Greeting and Introduction */}
            <div className="space-y-6">
              <h1 className="text-4xl md:text-6xl font-bold text-gray-900 dark:text-white">
                Hello! <span className="text-primary-500">(:</span>
              </h1>
              <p className="text-xl text-gray-600 dark:text-gray-300">
                I'm a Frontend Engineer with 6+ years of experience, focused on building AI-powered applications and data-driven user interfaces.
              </p>
              <div className="space-y-4">
                <p className="text-lg text-gray-600 dark:text-gray-400">
                  I design clean, scalable UIs and integrate AI features such as document analysis, structured data extraction, and human-in-the-loop workflows.
                </p>
                <p className="text-lg text-gray-600 dark:text-gray-400">
                  I'm passionate about building products that combine great UX with intelligent automation.
                </p>
              </div>
            </div>


            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  Top Skills:
                </h2>
              </div>
              <div className="flex flex-wrap gap-4">
                {topSkills.map((skill, index) => (
                  <motion.div
                    key={skill}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="text-gray-600 dark:text-gray-400 flex items-center space-x-2"
                  >
                    <span className="text-yellow-400">•</span>
                    <span>{skill}</span>
                  </motion.div>
                ))}
              </div>
            </div>
            <div className="pt-8"></div>

          </motion.div>
        </div>
      </section>

      {/* Featured Projects Section */}
      <section className="pt-20 pb-16 bg-gray-100 dark:bg-custom-800">
        <div className="max-w-6xl mx-auto px-2">


          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {[
              {
                title: "AI Document Assistant",
                description: "Upload contracts and policies, generate AI-powered summaries, extract structured fields, and verify results before they flow into downstream systems. Analysis runs server-side using the OpenAI API, with a focused, human-in-the-loop document review workflow.",
                tech: ["React", "TypeScript", "OpenAI API"],
                image: "📄",
                link: "https://ai-document-assistant7.netlify.app/"
              },
              {
                title: "Lottery App",
                description: "Decentralized lottery powered by Ethereum smart contracts and a React frontend.",
                tech: ["React", "Solidity", "Web3.js", "Ethereum"],
                image: "🎲",
                link: "https://github.com/shejza/lottery-app"
              },
              {
                title: "My Reads",
                description: "A bookshelf app for categorizing books (read, currently reading, want to read) using React and API server.",
                tech: ["React", "JavaScript", "API", "SCSS"],
                image: "📚",
                link: "https://github.com/shejza/my-reads"
              }
            ].map((project, index) => (
              <motion.div
                key={project.title}
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                className="bg-white dark:bg-custom-800 rounded-lg hover:shadow-lg transition-all duration-300 group p-6 border border-gray-200/50 dark:border-custom-700/50"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-lg mb-4 flex items-center justify-center text-2xl">
                  {project.image}
                </div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                  {project.title}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-2">
                  {project.description}
                </p>
                <div className="flex flex-wrap gap-1 mb-3">
                  {project.tech.slice(0, 3).map((tech) => (
                    <span
                      key={tech}
                      className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs rounded-full"
                    >
                      {tech}
                    </span>
                  ))}
                  {project.tech.length > 3 && (
                    <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs rounded-full">
                      +{project.tech.length - 3}
                    </span>
                  )}
                </div>
                <a
                  href={project.link}
                  className="inline-flex items-center text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-sm font-medium"
                >
                  View Project →
                </a>
              </motion.div>
            ))}
          </div>

          <div className="text-center">
            <Link
              to="/projects"
              className="inline-flex items-center space-x-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors duration-200"
            >
              <span>View More Projects</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">


          <div className="relative max-w-4xl mx-auto">
            <div className="overflow-hidden">
              <div className="flex transition-transform duration-500 ease-in-out" style={{ transform: `translateX(-${currentTestimonial * 100}%)` }}>
                {[
                                  {
                  quote: highlightWords("I had the pleasure of working with Shejza for over three years at ThriveCoin, where she was a Front-End Developer on our team. She consistently showed dedication and responsibility, always stepping in when needed and contributing with reliability and a positive attitude. Shejza has a strong passion for her work, and it really shows in her UI skills. She has an excellent eye for design and detail, and she consistently delivered clean, elegant, and user-friendly interfaces. Her ability to bring design concepts to life made a real impact on our product and the user experience. Beyond her technical abilities, working with Shejza was a great experience. She brought positive energy to the team and made collaboration easy and enjoyable. I'm confident that Shejza will bring value to any team she joins, and I wish her all the best in her next chapter.", 
                    ["three years", "ThriveCoin", "Front-End Developer", "dedication and responsibility", "reliability and a positive attitude", "passion for her work", "UI skills", "eye for design and detail", "clean, elegant, and user-friendly interfaces", "bring design concepts to life", "positive energy", "collaboration easy and enjoyable", "value to any team"]),
                  author: "Rilind Mehmeti",
                  role: "CTO & Co-Founder",
                  company: "Thrive Protocol",
                  linkedin: "https://www.linkedin.com/in/rilind-mehmeti/"
                },
                                  {
                  quote: highlightWords("I've worked with Shejza at Visme, and it was a great pleasure to see how quickly and with such enthusiasm she was always ready to help or explore new areas of web engineering. I highly recommend Shejza as a Full Stack Engineer for any software product, regardless of size or complexity.",
                    ["Visme", "quickly and with such enthusiasm", "web engineering", "Full Stack Engineer"]),
                  author: "Vadim Rastyagaev",
                  role: "Team Lead & Full-Stack Engineer",
                  company: "Visme",
                  linkedin: "https://www.linkedin.com/in/vadim-rastyagaev/"
                },
                                  {
                  quote: highlightWords("Shejza is a highly skilled and diligent software developer, and has, and continues to prove that she can handle any task large or small. Her ability to quickly understand tasks and provide solutions means that she will be invaluable to any organization or project that she chooses to join in the future. I recommend Shejza for any web/software project needs you may have.",
                    ["highly skilled and diligent", "any task large or small", "quickly understand tasks and provide solutions", "invaluable to any organization", "web/software project needs"]),
                  author: "Lloyd Emelle",
                  role: "Maker of things",
                  company: "LinkedIn",
                  linkedin: "https://www.linkedin.com/in/lloyd-emelle/"
                },
                                  {
                  quote: highlightWords("Shejza is a talented developer with great communication skills. She is a joy to work with. She helped us to quickly and easily build a working mobile application from a prototype design.",
                    ["talented developer", "great communication skills", "joy to work with", "quickly and easily", "working mobile application", "prototype design"]),
                  author: "Gavin Bintz",
                  role: "Co-Founder and Technical Director",
                  company: "Swirl Creative - US",
                  linkedin: ""
                }
                ].map((testimonial, index) => (
                  <div key={testimonial.author} className="w-full flex-shrink-0 px-4">
                    <motion.div
                      initial={{ opacity: 0, y: 50 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.8, delay: index * 0.2 }}
                      className="text-center min-h-96 p-8 border border-gray-200/40 dark:border-custom-700/40 rounded-lg flex flex-col"
                    >
                      {/* Quote Section */}
                      <div className="flex-1">
                        <div className="text-yellow-400 text-3xl mb-4">"</div>
                        <div className="text-gray-900 dark:text-gray-100 text-base leading-relaxed">
                          {index === 0 ? (
                            // Rilind's testimonial - show read more
                            expandedTestimonials[index] ? (
                              <p dangerouslySetInnerHTML={{ __html: testimonial.quote }}></p>
                            ) : (
                              <div>
                                <p className="line-clamp-3" dangerouslySetInnerHTML={{ __html: testimonial.quote }}></p>
                                <button
                                  onClick={() => toggleTestimonial(index)}
                                  className="inline-flex items-center text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-sm font-medium mt-2"
                                >
                                  Read More
                                  <ChevronDown className="w-4 h-4 ml-1" />
                                </button>
                              </div>
                            )
                          ) : (
                            // Vadim and Lloyd's testimonials - show full text
                            <p dangerouslySetInnerHTML={{ __html: testimonial.quote }}></p>
                          )}
                          {index === 0 && expandedTestimonials[index] && (
                            <button
                              onClick={() => toggleTestimonial(index)}
                              className="inline-flex items-center text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-sm font-medium mt-2"
                            >
                              Read Less
                              <ChevronUp className="w-4 h-4 ml-1" />
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Author Section - Always visible at bottom */}
                      <div className="mt-8 flex flex-col items-center space-y-3">
                        <div className="w-16 h-16 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center text-gray-600 dark:text-gray-300 font-bold text-xl">
                          {testimonial.author.charAt(0)}
                        </div>
                        <div className="text-center">
                          <h4 className="font-semibold text-gray-900 dark:text-white text-lg">
                            {testimonial.author}
                          </h4>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                            {testimonial.role} at {testimonial.company}
                          </p>
                          {testimonial.linkedin && (
                            <a
                              href="https://www.linkedin.com/in/shejza-berisha-514972a0/details/recommendations/?detailScreenTabIndex=0"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center text-blue-500 dark:text-blue-400 hover:text-blue-600 dark:hover:text-blue-300 text-sm font-medium"
                            >
                              <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                              </svg>
                              View Recommendation
                            </a>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Navigation Dots */}
            <div className="flex justify-center space-x-1 mt-6">
              {[0, 1, 2, 3].map((index) => (
                <button
                  key={index}
                  onClick={() => setCurrentTestimonial(index)}
                  className={`w-2 h-2 rounded-full transition-colors duration-200 ${
                    currentTestimonial === index
                      ? 'bg-gray-600 dark:bg-gray-300'
                      : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                />
              ))}
            </div>
            
            {/* Navigation Arrows */}
            <button
              onClick={() => setCurrentTestimonial((prev) => (prev === 0 ? 3 : prev - 1))}
              className="absolute left-2 top-1/2 transform -translate-y-1/2 w-10 h-10 flex items-center justify-center hover:scale-110 transition-all duration-200"
            >
              <svg className="w-6 h-6 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={() => setCurrentTestimonial((prev) => (prev === 3 ? 0 : prev + 1))}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 w-10 h-10 flex items-center justify-center hover:scale-110 transition-all duration-200"
            >
              <svg className="w-6 h-6 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      </section>



      {/* CTA Section */}
      <section className="pt-12 md:pt-16 pb-8 md:pb-10 bg-gray-100 dark:bg-custom-800">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-4 md:space-y-6"
          >
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-primary-600 dark:text-primary-400">
              I can help.
            </h2>
            <p className="text-base md:text-lg lg:text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto leading-relaxed px-4">
              I'm currently available for freelance work. If you have a project that you want to get started, think you need my help with something or just fancy saying hey, then get in touch.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-2 text-base md:text-lg lg:text-xl text-gray-600 dark:text-gray-400">
              <div className="flex items-center gap-2">
                <Mail className="w-5 h-5 md:w-6 md:h-6 text-yellow-400 flex-shrink-0" />
                <a 
                  href="mailto:shejzaberisha@gmail.com"
                  className="font-medium hover:text-gray-800 dark:hover:text-gray-200 transition-colors duration-200 break-all"
                >
                  shejzaberisha@gmail.com
                </a>
              </div>
            </div>
            <div className="flex justify-center gap-3 md:gap-4 mt-4 md:mt-6">
              <motion.a
                href="https://www.linkedin.com/in/shejza-berisha-514972a0/"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2.5 md:p-3 rounded-lg bg-gray-100 dark:bg-custom-800 hover:bg-primary-100 dark:hover:bg-primary-900/20 text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-all duration-200"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                <Linkedin className="w-5 h-5 md:w-6 md:h-6" />
              </motion.a>
              <motion.a
                href="https://github.com/shejza"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2.5 md:p-3 rounded-lg bg-gray-100 dark:bg-custom-800 hover:bg-primary-100 dark:hover:bg-primary-900/20 text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-all duration-200"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                <Github className="w-5 h-5 md:w-6 md:h-6" />
              </motion.a>
              <motion.a
                href="https://x.com/ShejzaBerisha"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2.5 md:p-3 rounded-lg bg-gray-100 dark:bg-custom-800 hover:bg-primary-100 dark:hover:bg-primary-900/20 text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-all duration-200"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                <Twitter className="w-5 h-5 md:w-6 md:h-6" />
              </motion.a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  )
}

export default Home 